import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import client from '../../../shared/api/client';
import CheckOption from './CheckOption';
import Section from './Section';

// Genre/Agency have long tails; the rail only lists the top options by count, so
// this adds a typeahead (server lookup, global counts) to reach any option, and
// always renders currently-selected options so a selection can never be truncated away.
const SearchableCheckboxFacet = ({ title, facetKey, options, selected, onToggle, onClear, facetOptionsUrl, showCounts = true }) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = term.trim();
    if (!q) { setResults(null); setSearching(false); return undefined; }

    let active = true;
    setSearching(true);
    const id = setTimeout(() => {
      client
        .get(facetOptionsUrl, { params: { facet: facetKey, q } })
        .then((res) => { if (active) setResults(res.data.map((o) => ({ label: o.name, count: o.count }))); })
        .catch(() => { if (active) setResults([]); })
        .finally(() => { if (active) setSearching(false); });
    }, 250);
    return () => { active = false; clearTimeout(id); };
  }, [term, facetKey, facetOptionsUrl]);

  const base = results !== null ? results : options;
  const shownLabels = new Set(base.map((o) => o.label));
  const selectedExtra = selected.filter((s) => !shownLabels.has(s)).map((s) => ({ label: s, count: null }));
  const shown = [...selectedExtra, ...base];

  return (
    <Section title={title} badge={selected.length}>
      <input
        className="ab-facet-search"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder={`Search ${title.toLowerCase()}…`}
        aria-label={`Search ${title.toLowerCase()}`}
      />
      <div className="ab-opts">
        {shown.map(({ label, count }) => (
          <CheckOption key={label} label={label} count={showCounts ? count : null} on={selected.includes(label)} onClick={() => onToggle(label)} />
        ))}
        {searching && <div className="ab-empty">Searching…</div>}
        {!searching && shown.length === 0 && <div className="ab-empty">No matches</div>}
        {selected.length > 0 && <button type="button" className="ab-clear" onClick={onClear}>Clear</button>}
      </div>
    </Section>
  );
};
SearchableCheckboxFacet.propTypes = {
  title: PropTypes.string.isRequired,
  facetKey: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  selected: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  facetOptionsUrl: PropTypes.string.isRequired,
  showCounts: PropTypes.bool,
};

export default SearchableCheckboxFacet;
